/**
 * TemplateSelector - Template selection UI for building editor
 *
 * Displays available building templates as cards.
 * Currently only Single Unit Community is available.
 */

import { templateInfo, type TemplateType } from '@/lib/templates'
import { cn } from '@/lib/utils'

interface TemplateSelectorProps {
  onSelect: (templateType: TemplateType) => void | Promise<void>
}

export function TemplateSelector({ onSelect }: TemplateSelectorProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">Select Template</h3>
        <p className="text-sm text-muted-foreground">
          Choose a building template to start
        </p>
      </div>

      <div className="space-y-2">
        {templateInfo.map((template) => {
          const Icon = template.icon
          const isAvailable = template.available

          return (
            <button
              key={template.id}
              onClick={() => isAvailable && onSelect(template.id)}
              disabled={!isAvailable}
              className={cn(
                'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                isAvailable
                  ? 'hover:bg-accent hover:border-primary cursor-pointer'
                  : 'opacity-50 cursor-not-allowed bg-muted/50'
              )}
            >
              <div
                className={cn(
                  'flex-shrink-0 p-2 rounded-md',
                  isAvailable ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{template.name}</span>
                  {!isAvailable && (
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded">Coming Soon</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
